import { computed, Injectable, signal } from '@angular/core';
import { environment } from 'environments/environment';
import { decodeJwtPayload, roleClaimValues } from './jwt';
import { createPkceChallenge, randomUrlSafeString } from './pkce';
import { pictureClaim } from './profile-picture';
import { TokenEndpointError, shouldEndSession } from './token-refresh';
import { SessionProvider } from './session-provider';

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  token_type?: string;
}

interface HandshakeState {
  verifier: string;
  state: string;
  returnUrl: string;
}

const HANDSHAKE_KEY = 'checkins.oidc.handshake';
const EXPIRY_SKEW_MS = 30_000;

/**
 * Real OIDC session provider: authorization-code + PKCE against RockApps.Identity.
 *
 * Access and refresh tokens are held in memory only (never localStorage), so closing the tab or
 * logging out clears them — that's the "logged out, someone reopens the tab → no access"
 * protection. Only the one-time PKCE handshake (verifier + state) uses sessionStorage, because it
 * must survive the full-page redirect to the callback, and it is deleted right after the exchange.
 */
@Injectable({ providedIn: 'root' })
export class OidcSessionProvider implements SessionProvider {
  private readonly accessToken = signal<string | null>(null);
  private readonly rolesSignal = signal<string[]>([]);
  private readonly pictureSignal = signal<string | null>(null);
  private refreshToken: string | null = null;
  private idToken: string | null = null;
  private expiresAtMs = 0;
  private refreshInFlight: Promise<string | null> | null = null;

  readonly isAuthenticated = computed(() => this.accessToken() !== null);

  readonly roles = this.rolesSignal.asReadonly();

  readonly picture = this.pictureSignal.asReadonly();

  private get config() {
    return environment.auth;
  }

  getAccessToken(): string | null {
    return this.accessToken();
  }

  async getValidAccessToken(): Promise<string | null> {
    if (this.accessToken() === null) {
      return null;
    }
    if (Date.now() < this.expiresAtMs - EXPIRY_SKEW_MS) {
      return this.accessToken();
    }
    return this.refresh();
  }

  async login(returnUrl = '/'): Promise<void> {
    const verifier = randomUrlSafeString(32);
    const challenge = await createPkceChallenge(verifier);
    const state = randomUrlSafeString(16);

    const handshake: HandshakeState = { verifier, state, returnUrl };
    sessionStorage.setItem(HANDSHAKE_KEY, JSON.stringify(handshake));

    const params = new URLSearchParams({
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      response_type: 'code',
      scope: this.config.scope,
      state,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });
    window.location.assign(`${this.config.authority}/connect/authorize?${params.toString()}`);
  }

  async handleCallback(): Promise<string> {
    const query = new URLSearchParams(window.location.search);
    const code = query.get('code');
    const returnedState = query.get('state');

    const raw = sessionStorage.getItem(HANDSHAKE_KEY);
    sessionStorage.removeItem(HANDSHAKE_KEY);
    if (code === null || raw === null) {
      throw new Error('Missing authorization code or handshake state.');
    }

    const handshake = JSON.parse(raw) as HandshakeState;
    if (returnedState !== handshake.state) {
      throw new Error('State mismatch on login callback.');
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: this.config.redirectUri,
      client_id: this.config.clientId,
      code_verifier: handshake.verifier,
    });
    await this.exchange(body);
    return handshake.returnUrl;
  }

  logout(): void {
    const idTokenHint = this.idToken;
    this.clearTokens();

    const params = new URLSearchParams({
      post_logout_redirect_uri: this.config.postLogoutRedirectUri,
    });
    if (idTokenHint !== null) {
      params.set('id_token_hint', idTokenHint);
    }
    window.location.assign(`${this.config.authority}/connect/logout?${params.toString()}`);
  }

  private refresh(): Promise<string | null> {
    if (this.refreshInFlight !== null) {
      return this.refreshInFlight;
    }
    if (this.refreshToken === null) {
      this.clearTokens();
      return Promise.resolve(null);
    }

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: this.refreshToken,
      client_id: this.config.clientId,
    });
    this.refreshInFlight = this.exchange(body)
      .then(() => this.accessToken())
      .catch((error: unknown) => {
        // Only a refusal from Identity ends the session. A refresh that never left the device says
        // nothing about whether the refresh token is still good, and discarding it there is what
        // turned a brief drop in signal into a login redirect mid-check-in. The throw propagates so
        // the caller can tell "no token this time" from "signed out".
        if (!shouldEndSession(error)) {
          throw error;
        }

        this.clearTokens();
        return null;
      })
      .finally(() => {
        this.refreshInFlight = null;
      });
    return this.refreshInFlight;
  }

  private async exchange(body: URLSearchParams): Promise<void> {
    const response = await fetch(`${this.config.authority}/connect/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    if (!response.ok) {
      // Typed so the refresh path can tell a refusal from a failure to ask.
      throw new TokenEndpointError(response.status);
    }

    const tokens = (await response.json()) as TokenResponse;
    this.accessToken.set(tokens.access_token);
    this.rolesSignal.set(roleClaimValues(decodeJwtPayload(tokens.access_token)));
    this.refreshToken = tokens.refresh_token ?? this.refreshToken;
    this.idToken = tokens.id_token ?? this.idToken;
    // From the ID token, not the access token: Identity only puts `picture` in the ID token. A refresh
    // that returns no new ID token keeps the current one, and so the current picture.
    this.pictureSignal.set(pictureClaim(this.idToken === null ? null : decodeJwtPayload(this.idToken)));
    this.expiresAtMs = Date.now() + (tokens.expires_in ?? 0) * 1000;
  }

  private clearTokens(): void {
    this.accessToken.set(null);
    this.rolesSignal.set([]);
    this.pictureSignal.set(null);
    this.refreshToken = null;
    this.idToken = null;
    this.expiresAtMs = 0;
  }
}
